import React, { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import {
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaGlobe,
  FaServer,
  FaBuilding,
  FaHeadset,
  FaDiscord,
  FaTicketAlt,
  FaBook,
  FaPhone,
  FaServer as FaStatus,
} from "react-icons/fa";

/* ---------------------------------------
   1. Animations for Mobile Drawer
---------------------------------------- */
const slideInFromRight = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0.8;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutToRight = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0.8;
  }
`;

/* ---------------------------------------
   2. Mobile Drawer Backdrop
---------------------------------------- */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
  transition: opacity 0.3s ease;
`;

/* ---------------------------------------
   3. Mobile Drawer Container
---------------------------------------- */
const DrawerContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 200px;
  height: 100vh;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  z-index: 1100;
  overflow-y: auto;
  justify-content: center;

  /* Modern glass/gradient effect */
  background: transparent;
  backdrop-filter: blur(10px);
  box-shadow: -4px 0 14px rgba(0, 0, 0, 0.5);
  border-left: 1px solid rgba(255, 255, 255, 0.2);

  /* Slide in/out based on isOpen */
  animation: ${(props) =>
    props.isOpen ? slideInFromRight : slideOutToRight} 0.35s forwards;
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
`;

/* ---------------------------------------
   4. Close Button in Mobile Drawer
---------------------------------------- */
// const DrawerCloseButton = styled.button`
//   align-self: flex-end;
//   background: none;
//   border: none;
//   font-size: 1.6rem;
//   color: #ffffff;
//   cursor: pointer;
//   margin-bottom: 1rem;
//   transition: color 0.2s;

//   &:hover {
//     color: #3498db;
//   }
// `;

/* ---------------------------------------
   5. Drawer Navigation Items
---------------------------------------- */
const DrawerNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: -200px;
`;

const DrawerNavItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  color: #c1c3d7;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.25s, color 0.25s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const DrawerItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IconWrapper = styled.span`
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DrawerLabel = styled.span`
  font-size: 1rem;
  text-decoration: none;
  color: inherit;
`;

/* ---------------------------------------
   6. SubMenu (collapsible under "Support")
---------------------------------------- */
const SubMenuWrapper = styled.div`
  overflow: hidden;
  max-height: ${({ expanded }) => (expanded ? "300px" : "0")};
  transition: max-height 0.35s ease;
  margin-left: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const SubMenuItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  text-decoration: none;
  font-size: 0.92rem;
  color: #c1c3d7;
  transition: color 0.2s;

  &:hover {
    color: #3498db;
  }
`;

/* ---------------------------------------
   7. Buttons (side by side) right after Support
---------------------------------------- */
const DrawerButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 1rem 0; /* spacing around buttons */
`;

const PrimaryButton = styled.button`
  background-color: #3498db;
  color: #fff;
  border: none;
  padding: 0.8rem 1.2rem;
  font-size: 1rem;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;

  &:hover {
    background-color: #1d73c8;
    transform: scale(1.03);
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: #3498db;
  border: 2px solid #3498db;
  padding: 0.8rem 1.2rem;
  font-size: 1rem;
  border-radius: 5px;
  cursor: pointer;
  transition: color 0.3s, background-color 0.3s, transform 0.3s;

  &:hover {
    background-color: #3498db;
    color: #fff;
    transform: scale(1.03);
  }
`;

/* ---------------------------------------
   8. MobileDrawer Component
   (Handles the mobile nav + submenus)
---------------------------------------- */
function MobileDrawer({ isOpen, onClose }) {
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <>
      <Backdrop isOpen={isOpen} onClick={onClose} />

      <DrawerContainer isOpen={isOpen}>
        {/* <DrawerCloseButton onClick={onClose}>
          <FaTimes />
        </DrawerCloseButton> */}

        <DrawerNav>
          {/* Domains */}
          <DrawerNavItem onClick={onClose}>
            <DrawerItemLeft>
              <IconWrapper>
                <FaGlobe />
              </IconWrapper>
              <DrawerLabel>Domains</DrawerLabel>
            </DrawerItemLeft>
          </DrawerNavItem>

          {/* Web Hosting */}
          <DrawerNavItem onClick={onClose}>
            <DrawerItemLeft>
              <IconWrapper>
                <FaServer />
              </IconWrapper>
              <DrawerLabel>Web Hosting</DrawerLabel>
            </DrawerItemLeft>
          </DrawerNavItem>

          {/* Company */}
          <DrawerNavItem onClick={onClose}>
            <DrawerItemLeft>
              <IconWrapper>
                <FaBuilding />
              </IconWrapper>
              <DrawerLabel>Company</DrawerLabel>
            </DrawerItemLeft>
          </DrawerNavItem>

          {/* Support (Collapsible Submenu) */}
          <DrawerNavItem
            onClick={() => setSupportOpen((prev) => !prev)}
            style={{ marginBottom: supportOpen ? "0.5rem" : "0" }}
          >
            <DrawerItemLeft>
              <IconWrapper>
                <FaHeadset />
              </IconWrapper>
              <DrawerLabel>Support</DrawerLabel>
            </DrawerItemLeft>
            {supportOpen ? <FaChevronUp /> : <FaChevronDown />}
          </DrawerNavItem>

          {/* SubMenu Items */}
          <SubMenuWrapper expanded={supportOpen}>
            <SubMenuItem href="#" onClick={onClose}>
              <FaDiscord /> Join Discord
            </SubMenuItem>
            <SubMenuItem href="#" onClick={onClose}>
              <FaTicketAlt /> Submit Ticket
            </SubMenuItem>
            <SubMenuItem href="#" onClick={onClose}>
              <FaBook /> Documentation
            </SubMenuItem>
            <SubMenuItem href="#" onClick={onClose}>
              <FaPhone /> Contact Us
            </SubMenuItem>
            <SubMenuItem href="#" onClick={onClose}>
              <FaStatus /> Server Status
            </SubMenuItem>
          </SubMenuWrapper>

          {/* 
            Side-by-side Buttons
            Right after Support (NOT pinned at bottom)
          */}
          <DrawerButtonGroup>
            <PrimaryButton onClick={onClose}>Sign Up</PrimaryButton>
            <SecondaryButton onClick={onClose}>Login</SecondaryButton>
          </DrawerButtonGroup>
        </DrawerNav>
      </DrawerContainer>
    </>
  );
}

/* ---------------------------------------
   9. Desktop Navbar (sticky at top)
---------------------------------------- */
const NavbarSection = styled.div`
  position: sticky;
  top: 0;
  z-index: 999;
  background: transparent;
  backdrop-filter: ${(props) => (props.scrolled ? "blur(10px)" : "none")};
  box-shadow: ${(props) =>
    props.scrolled ? "0 1.5px 6.9px #3498db" : "none"};
  padding: 1rem 1.5rem;
  height: 54px;
`;

const NavbarContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/* ---------------------------------------
   10. Logo Section
---------------------------------------- */
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const PictureFrame = styled.div`
  height: 50px;
  width: 50px;
  border-radius: 50%;
  background-color: transparent;
  border: 2px solid #3498db;
  overflow: hidden;

  &:hover {
    border: 2px solid #1d73c8;
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoText = styled.a`
  text-decoration: none;
  color: #3498db;
  font-size: 1.5rem;
  font-weight: bold;

  &:hover {
    color: #1d73c8;
  }
`;

/* ---------------------------------------
   11. Desktop Nav + "Support" Dropdown
---------------------------------------- */
const DesktopNav = styled.nav`
  @media (max-width: 1240px) {
    display: none;
  }
  display: flex;
  gap: 2rem;
`;

const NavItem = styled.div`
  position: relative;
`;

const NavLink = styled.a`
  text-decoration: none;
  color: #c1c3d7;
  font-size: 1rem;
  font-weight: bold;
  transition: color 0.3s;

  &:hover {
    color: #3498db;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: transparent;
  border: 1px solid #c1c3d7;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  display: none;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 200;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);

  ${NavItem}:hover & {
    display: flex;
  }
`;

const DropdownLink = styled.a`
  text-decoration: none;
  color: #c1c3d7;
  font-size: 0.9rem;
  transition: color 0.3s;

  &:hover {
    color: #3498db;
  }
`;

/* ---------------------------------------
   12. Desktop Button Group
---------------------------------------- */
const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 1240px) {
    display: none;
  }
`;

/* ---------------------------------------
   13. Hamburger for Mobile
---------------------------------------- */
const Hamburger = styled.div`
  display: none;
  cursor: pointer;
  width: 30px;
  height: 22px;
  position: relative;

  @media (max-width: 1240px) {
    display: block;
  }

  span {
    display: block;
    height: 3px;
    width: 100%;
    background: #c1c3d7;
    border-radius: 2px;
    margin-bottom: 5px;
    transition: all 0.3s ease;
    &:last-child {
      margin-bottom: 0;
    }
  }

  ${(props) =>
    props.isOpen &&
    css`
      span:nth-child(1) {
        transform: translateY(8px) rotate(45deg);
        background: #3498db;
      }
      span:nth-child(2) {
        opacity: 0;
      }
      span:nth-child(3) {
        transform: translateY(-8px) rotate(-45deg);
        background: #3498db;
      }
    `}
`;

/* ---------------------------------------
   14. Main Navbar Component
---------------------------------------- */
function Navbar() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Detect scroll to add blur/shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isDrawerOpen]);

  // Close drawer if resized above mobile breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1240) {
        setIsDrawerOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {/* Sticky top navbar (Desktop) */}
      <NavbarSection scrolled={scrolled}>
        <NavbarContainer>
          {/* Logo */}
          <LogoContainer>
            <PictureFrame>
              {/* Replace with your own logo path */}
              <LogoImage src="assets/logotest.png" alt="Logo" />
            </PictureFrame>
            <LogoText href="/">simonfoy.host</LogoText>
          </LogoContainer>

          {/* Desktop Nav Items + "Support" dropdown */}
          <DesktopNav>
            <NavItem>
              <NavLink href="/">Domains</NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="/">Web Hosting</NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="/">Company</NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="/">Support</NavLink>
              <Dropdown>
                <DropdownLink href="#">Join Discord</DropdownLink>
                <DropdownLink href="#">Submit Ticket</DropdownLink>
                <DropdownLink href="#">Documentation</DropdownLink>
                <DropdownLink href="#">Contact Us</DropdownLink>
                <DropdownLink href="#">Server Status</DropdownLink>
              </Dropdown>
            </NavItem>
          </DesktopNav>

          {/* Desktop Buttons */}
          <ButtonGroup>
            <PrimaryButton>Sign Up</PrimaryButton>
            <SecondaryButton>Login</SecondaryButton>
          </ButtonGroup>

          {/* Hamburger for Mobile */}
          <Hamburger
            isOpen={isDrawerOpen}
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
          >
            <span />
            <span />
            <span />
          </Hamburger>
        </NavbarContainer>
      </NavbarSection>

      {/* Modern, sleek mobile drawer */}
      <MobileDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </>
  );
}

export default Navbar;
